import React, { useState, useEffect, useRef } from 'react';
import { apiDelete, apiGet, apiPost, apiPut } from '../api';
import { DashboardLayout } from '../components/Layout';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Textarea } from '../components/ui/textarea';
import { 
  Users, Plus, Edit, Trash2, Loader2, Mail, User, Search, Upload, Camera
} from 'lucide-react';
import { toast } from 'sonner';
import { formatApiError } from '../lib/apiError';
import { compressImage } from '../lib/imageCompress';

export const StudioInstructorsPage = () => {
  const [studio, setStudio] = useState(null);
  const [instructors, setInstructors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');

  // Dialog states
  const [showDialog, setShowDialog] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [selectedInstructor, setSelectedInstructor] = useState(null);
  const [formLoading, setFormLoading] = useState(false);

  // Form state
  const [form, setForm] = useState({
    first_name: '',
    last_name: '',
    email: '',
    password: '',
    bio: '',
    specialties: '',
    hourly_rate: 0,
    photo_url: ''
  });
  
  // Photo upload
  const fileInputRef = useRef(null);
  const [uploading, setUploading] = useState(false);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [studioRes, instructorsRes] = await Promise.all([
        apiGet('/api/studios/me'),
        apiGet('/api/instructors')
      ]);
      setStudio(studioRes);
      setInstructors(instructorsRes || []);
    } catch (e) {
      console.error('Failed to load data:', e);
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setForm({
      first_name: '',
      last_name: '',
      email: '',
      password: '',
      bio: '',
      specialties: '',
      hourly_rate: 0,
      photo_url: ''
    });
    setIsEditing(false);
    setSelectedInstructor(null);
  };

  const handleOpenCreate = () => {
    resetForm();
    setShowDialog(true);
  };

  const handleOpenEdit = (instructor) => {
    setSelectedInstructor(instructor);
    setForm({
      first_name: instructor.first_name,
      last_name: instructor.last_name,
      email: instructor.email,
      password: '', // Don't pre-fill password
      bio: instructor.bio || '',
      specialties: (instructor.specialties || []).join(', '),
      hourly_rate: instructor.hourly_rate || 0,
      photo_url: instructor.photo_url || ''
    });
    setIsEditing(true);
    setShowDialog(true);
  };

  const handlePhotoUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    // Validate file type
    if (!file.type.startsWith('image/')) {
      toast.error('Please select an image file');
      return;
    }
    
    // Validate file size (max 5MB)
    if (file.size > 5 * 1024 * 1024) {
      toast.error('Image must be less than 5MB');
      return;
    }
    
    setUploading(true);
    try {
      // Bug R20 fix (Iter-68): compress client-side so we stay under the
      // ingress proxy-body-size ceiling.
      let uploadFile = file;
      try { uploadFile = await compressImage(file); } catch (_) { /* fall through */ }
      // Iter-46h: backend expects `entity_type` (was `type`). For edit flow
      // send the instructor id so the file is attached immediately; for the
      // create flow omit it — the url goes into the form and is saved on
      // submit.
      const formData = new FormData();
      formData.append('file', uploadFile);
      formData.append('entity_type', 'instructor');
      if (isEditing && selectedInstructor?.id) {
        formData.append('entity_id', selectedInstructor.id);
      }

      const data = await apiPost('/api/upload/image', { body: formData });

      setForm({ ...form, photo_url: data.url });
      toast.success('Photo uploaded!');
    } catch (e) {
      const msg = formatApiError(e, 'Failed to upload photo');
      toast.error(msg);
    } finally {
      setUploading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.first_name || !form.last_name || !form.email) {
      toast.error('Please fill in all required fields');
      return;
    }
    
    setFormLoading(true);
    try {
      const payload = {
        studio_id: studio.id,
        first_name: form.first_name,
        last_name: form.last_name,
        email: form.email,
        bio: form.bio,
        specialties: form.specialties.split(',').map(s => s.trim()).filter(Boolean),
        hourly_rate: form.hourly_rate,
        photo_url: form.photo_url || null,
        ...(form.password && { password: form.password })
      };

      if (isEditing && selectedInstructor) {
        await apiPut('/api/instructors/{instructor_id}', { params: { instructor_id: selectedInstructor.id }, body: payload });
        toast.success('Instructor updated!');
      } else {
        await apiPost('/api/instructors', { body: payload });
        toast.success('Instructor added!');
      }
      setShowDialog(false);
      resetForm();
      fetchData();
    } catch (e) {
      toast.error(formatApiError(e, 'Failed to save instructor'));
    } finally {
      setFormLoading(false);
    }
  };

  const handleDelete = async (instructorId) => {
    if (!window.confirm('Remove this instructor? They will no longer appear in class assignments.')) return;
    try {
      await apiDelete('/api/instructors/{instructor_id}', { params: { instructor_id: instructorId } });
      toast.success('Instructor removed');
      fetchData();
    } catch (e) {
      toast.error('Failed to remove instructor');
    }
  };

  const filteredInstructors = instructors.filter(instructor => {
    if (!searchTerm) return true;
    const term = searchTerm.toLowerCase();
    return (
      instructor.first_name?.toLowerCase().includes(term) ||
      instructor.last_name?.toLowerCase().includes(term) ||
      instructor.email?.toLowerCase().includes(term) ||
      (instructor.specialties || []).some(s => s.toLowerCase().includes(term))
    );
  });

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center h-64">
          <Loader2 className="w-8 h-8 animate-spin text-primary" />
        </div>
      </DashboardLayout>
    );
  }

  if (!studio) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center h-64">
          <Card className="max-w-md">
            <CardContent className="p-6 text-center">
              <p className="text-muted-foreground">Please create a studio first</p>
            </CardContent>
          </Card>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="space-y-6" data-testid="studio-instructors-page">
        <div className="flex justify-between items-center">
          <div>
            <h1 className="text-3xl font-bold">Instructors</h1>
            <p className="text-muted-foreground">Manage your studio's instructors</p>
          </div>
          <Button onClick={handleOpenCreate} data-testid="add-instructor-btn">
            <Plus className="w-4 h-4 mr-2" />
            Add Instructor
          </Button>
        </div>

        {/* Search */}
        <Card>
          <CardContent className="p-4">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              <Input 
                placeholder="Search by name, email, or specialty..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10"
                data-testid="instructor-search"
              />
            </div>
          </CardContent>
        </Card>

        {/* Instructors Grid */}
        {filteredInstructors.length > 0 ? (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
            {filteredInstructors.map((instructor, i) => (
              <Card key={instructor.id} data-testid={`instructor-card-${i}`}>
                <CardContent className="p-4">
                  <div className="flex items-start gap-4">
                    <div className="w-16 h-16 rounded-full bg-primary/10 flex items-center justify-center overflow-hidden flex-shrink-0">
                      {instructor.photo_url ? (
                        <img 
                          src={instructor.photo_url.startsWith('/api') ? `${process.env.REACT_APP_BACKEND_URL}${instructor.photo_url}` : instructor.photo_url}
                          alt={instructor.first_name}
                          className="w-full h-full object-cover"
                        />
                      ) : (
                        <User className="w-8 h-8 text-primary" />
                      )}
                    </div>
                    <div className="flex-1 min-w-0">
                      <h3 className="font-semibold truncate">
                        {instructor.first_name} {instructor.last_name}
                      </h3>
                      <p className="text-sm text-muted-foreground truncate flex items-center gap-1">
                        <Mail className="w-3 h-3" />
                        {instructor.email}
                      </p>
                      {instructor.specialties?.length > 0 && (
                        <div className="flex flex-wrap gap-1 mt-2">
                          {instructor.specialties.slice(0, 3).map((specialty, j) => (
                            <Badge key={j} variant="secondary" className="text-xs">
                              {specialty}
                            </Badge>
                          ))}
                          {instructor.specialties.length > 3 && (
                            <Badge variant="outline" className="text-xs">
                              +{instructor.specialties.length - 3}
                            </Badge>
                          )}
                        </div>
                      )}
                      {instructor.bio && (
                        <p className="text-sm text-muted-foreground mt-2 line-clamp-2">
                          {instructor.bio}
                        </p>
                      )}
                    </div>
                  </div>
                  <div className="flex gap-2 mt-4 pt-4 border-t">
                    <Button 
                      variant="outline" 
                      size="sm" 
                      className="flex-1"
                      onClick={() => handleOpenEdit(instructor)}
                      data-testid={`edit-instructor-${i}`}
                    >
                      <Edit className="w-4 h-4 mr-1" />
                      Edit
                    </Button>
                    <Button 
                      variant="outline" 
                      size="sm"
                      onClick={() => handleDelete(instructor.id)}
                      data-testid={`delete-instructor-${i}`}
                    >
                      <Trash2 className="w-4 h-4 text-destructive" />
                    </Button>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        ) : (
          <Card className="p-12 text-center">
            <Users className="w-16 h-16 mx-auto text-muted-foreground/30 mb-4" />
            <h3 className="text-lg font-semibold mb-2">No instructors yet</h3>
            <p className="text-muted-foreground mb-4">
              Add instructors to assign them to your classes
            </p>
            <Button onClick={handleOpenCreate}>
              <Plus className="w-4 h-4 mr-2" />
              Add Your First Instructor
            </Button>
          </Card>
        )}
      </div>

      {/* Add/Edit Instructor Dialog */}
      <Dialog open={showDialog} onOpenChange={setShowDialog}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>{isEditing ? 'Edit Instructor' : 'Add New Instructor'}</DialogTitle>
            <DialogDescription>
              {isEditing 
                ? 'Update the instructor details below'
                : 'Add a new instructor to your studio. They can optionally have login access.'
              }
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4">
            {/* Photo Upload */}
            <div className="flex items-center gap-4">
              <div className="relative">
                <div className="w-20 h-20 rounded-full bg-primary/10 flex items-center justify-center overflow-hidden border-2 border-dashed border-muted-foreground/30">
                  {form.photo_url ? (
                    <img 
                      src={form.photo_url.startsWith('/api') ? `${process.env.REACT_APP_BACKEND_URL}${form.photo_url}` : form.photo_url}
                      alt="Instructor"
                      className="w-full h-full object-cover"
                    />
                  ) : (
                    <User className="w-8 h-8 text-muted-foreground" />
                  )}
                </div>
                {uploading && (
                  <div className="absolute inset-0 bg-background/80 rounded-full flex items-center justify-center">
                    <Loader2 className="w-6 h-6 animate-spin text-primary" />
                  </div>
                )}
              </div>
              <div className="flex-1">
                <input
                  type="file"
                  ref={fileInputRef}
                  onChange={handlePhotoUpload}
                  accept="image/*"
                  className="hidden"
                />
                <Button 
                  type="button" 
                  variant="outline" 
                  size="sm"
                  onClick={() => fileInputRef.current?.click()}
                  disabled={uploading}
                  data-testid="upload-photo-btn"
                >
                  {uploading ? (
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  ) : (
                    <Camera className="w-4 h-4 mr-2" />
                  )}
                  {form.photo_url ? 'Change Photo' : 'Upload Photo'}
                </Button>
                <p className="text-xs text-muted-foreground mt-1">Max 5MB, JPG or PNG</p>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>First Name *</Label>
                <Input 
                  value={form.first_name}
                  onChange={(e) => setForm({ ...form, first_name: e.target.value })}
                  placeholder="John"
                  required
                  data-testid="instructor-first-name"
                />
              </div>
              <div className="space-y-2">
                <Label>Last Name *</Label>
                <Input 
                  value={form.last_name}
                  onChange={(e) => setForm({ ...form, last_name: e.target.value })}
                  placeholder="Doe"
                  required
                  data-testid="instructor-last-name"
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label>Email *</Label>
              <Input 
                type="email"
                value={form.email}
                onChange={(e) => setForm({ ...form, email: e.target.value })}
                placeholder="john@example.com"
                required
                data-testid="instructor-email"
              />
            </div>

            <div className="space-y-2">
              <Label>Password {isEditing ? '(leave blank to keep current)' : '(optional - for login access)'}</Label>
              <Input 
                type="password"
                value={form.password}
                onChange={(e) => setForm({ ...form, password: e.target.value })}
                placeholder={isEditing ? '••••••••' : 'Enter password for login access'}
                data-testid="instructor-password"
              />
              <p className="text-xs text-muted-foreground">
                {isEditing 
                  ? 'Leave blank to keep existing password'
                  : 'If provided, instructor can log in to view their schedule'
                }
              </p>
            </div>

            <div className="space-y-2">
              <Label>Specialties (comma-separated)</Label>
              <Input 
                value={form.specialties}
                onChange={(e) => setForm({ ...form, specialties: e.target.value })}
                placeholder="Yoga, Pilates, HIIT"
                data-testid="instructor-specialties"
              />
            </div>

            <div className="space-y-2">
              <Label>Bio</Label>
              <Textarea 
                value={form.bio}
                onChange={(e) => setForm({ ...form, bio: e.target.value })}
                placeholder="Brief biography..."
                rows={3}
                data-testid="instructor-bio"
              />
            </div>

            <div className="space-y-2">
              <Label>Hourly Rate (€)</Label>
              <Input 
                type="number"
                step="0.01"
                value={form.hourly_rate || ''}
                onChange={(e) => setForm({ ...form, hourly_rate: parseFloat(e.target.value) || 0 })}
                min={0}
                placeholder="0.00"
                data-testid="instructor-rate"
              />
            </div>

            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setShowDialog(false)}>
                Cancel
              </Button>
              <Button type="submit" disabled={formLoading} data-testid="save-instructor-btn">
                {formLoading ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : isEditing ? (
                  'Save Changes'
                ) : (
                  'Add Instructor'
                )}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </DashboardLayout>
  );
};
